/**
 * Asoebi guest-cost accounting — does what the asoebi package actually earns
 * cover what it costs to have that guest at the wedding at all (the fabric
 * itself, plus their share of catering and their seat)?
 *
 * The two "hosting cost" categories are matched by budget category NAME
 * (Catering + Drinks & Beverages = food/drink; Venue = seating/space) rather
 * than by a dedicated flag, since that's the only signal budget_categories
 * carries today. Decoration is deliberately excluded — it's an aesthetic
 * spend, not a per-guest hosting cost, so folding it in would overstate what
 * asoebi actually needs to cover.
 *
 * Two guest-count bases are shown side by side rather than picked for the
 * user: `events.headcount` is the max number of people the couple plans to
 * INVITE to that event — a planning ceiling on the invite list, NOT the
 * venue's physical capacity (that's `venues.capacity` /
 * `capacity_seated_banquet` etc., a separate table this module never reads),
 * while confirmed-guests-so-far is what's actually been invited and
 * confirmed today. Spreading hosting cost over the max invite list gives
 * the best-case (lowest) cost-per-guest — the number if every invited guest
 * actually shows and asoebi is bought at the same rate; spreading it over
 * confirmed guests gives today's real, usually much higher, cost-per-guest.
 * They can differ by an order of magnitude this early in planning, so
 * collapsing them into one number would hide which one you're looking at.
 */
import { BudgetCategory } from '../types/ui/Budget';
import { StoredEvent } from '../services/EventsService';
import { GuestStats } from '../types/ui/Guests';

const FOOD_AND_DRINK_CATEGORY_NAMES = ['Catering', 'Drinks & Beverages'];
const SEATING_CATEGORY_NAMES = ['Venue'];

export interface HostingCostBreakdown {
  foodAndDrinks: number;
  seatingAndArrangements: number;
  total: number;
  /** Sum of max INVITE LIST size (events.headcount — not venue capacity)
   *  across whichever events asoebi bundles are tagged to — a ceiling on
   *  who's invited, not a real guest list or a target. */
  plannedGuestBasis: number;
  /** Confirmed guests + their confirmed plus-ones, wedding-wide — the
   *  closest real number available without per-event RSVP data client-side. */
  actualGuestBasis: number;
}

function sumByName(categories: BudgetCategory[], names: string[]): number {
  return categories
    .filter(c => !c.archived && names.includes(c.name))
    .reduce((sum, c) => sum + (c.actual_amount || 0), 0);
}

export function computeHostingCostBreakdown(
  categories: BudgetCategory[],
  events: StoredEvent[],
  asoebiEventIds: string[],
  guestStats: GuestStats | null,
): HostingCostBreakdown {
  const foodAndDrinks = sumByName(categories, FOOD_AND_DRINK_CATEGORY_NAMES);
  const seatingAndArrangements = sumByName(categories, SEATING_CATEGORY_NAMES);

  const taggedEvents = asoebiEventIds.length > 0
    ? events.filter(e => asoebiEventIds.includes(e.id))
    : events;
  const plannedGuestBasis = taggedEvents.reduce((sum, e) => sum + (e.headcount || 0), 0);

  const actualGuestBasis = guestStats
    ? guestStats.confirmedGuests + guestStats.totalPlusOnes
    : 0;

  return {
    foodAndDrinks,
    seatingAndArrangements,
    total: foodAndDrinks + seatingAndArrangements,
    plannedGuestBasis,
    actualGuestBasis,
  };
}

export interface GuestCostCoverage {
  /** Realised average sale price per completed assignment. */
  avgPackagePrice: number;
  /** Realised average fabric cost per completed assignment. */
  avgPackageCost: number;
  hostingCostPerGuest: number;
  /** avgPackagePrice - avgPackageCost - hostingCostPerGuest. Negative means
   *  the guest is a net loss once hosting cost is counted, even if the
   *  package alone looked profitable. */
  netMargin: number;
  /** What share of (package cost + hosting cost) the package price covers,
   *  as a percentage. 100 = breakeven; below 100 = the guest costs more to
   *  host than their asoebi brings in. */
  coveragePct: number;
}

export function computeGuestCostCoverage(
  avgPackagePrice: number,
  avgPackageCost: number,
  hostingCostPerGuest: number,
): GuestCostCoverage {
  const totalCost = avgPackageCost + hostingCostPerGuest;
  return {
    avgPackagePrice,
    avgPackageCost,
    hostingCostPerGuest,
    netMargin: avgPackagePrice - totalCost,
    coveragePct: totalCost > 0 ? (avgPackagePrice / totalCost) * 100 : 100,
  };
}
